import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Navbar = ({ cartCount, onOpenCart }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop', href: '#shop' },
    { name: 'Rituals', href: '#rituals' },
    { name: 'Philosophy', href: '#philosophy' }
  ];

  return (
    <>
      <motion.header
        className={`fixed top-0 w-full z-50 transition-all duration-700 ${
          scrolled ? 'bg-white/90 backdrop-blur-xl py-4 shadow-sm' : 'bg-transparent py-6'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex-1 hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href} 
                className="text-[10px] font-bold tracking-[0.3em] uppercase hover:text-stone-400 transition-colors focus:outline-none focus:ring-1 focus:ring-stone-900 px-1"
              >
                {link.name}
              </a>
            ))}
          </div>

          <div className="md:hidden flex-1">
            <button 
              onClick={() => setMobileMenuOpen(true)} 
              className="p-2 -ml-2"
              aria-label="Open navigation menu"
            >
              <SafeIcon icon={FiIcons.FiMenu} className="w-5 h-5" />
            </button>
          </div>

          <a href="#" className="font-serif text-2xl md:text-3xl tracking-tighter text-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900 px-2">
            GlowDock<span className="text-stone-400">.</span>
          </a>

          <div className="flex-1 flex justify-end items-center gap-8">
            <button className="hidden lg:block text-[10px] font-bold tracking-[0.3em] uppercase hover:text-stone-400 transition-colors">Search</button>
            <button 
              onClick={onOpenCart}
              className="relative flex items-center gap-3 hover:text-stone-400 transition-colors focus:outline-none focus:ring-1 focus:ring-stone-900 px-1"
              aria-label={`Open cart with ${cartCount} items`}
            >
              <span className="hidden md:block text-[10px] font-bold tracking-[0.3em] uppercase">Bag</span>
              <div className="relative">
                <SafeIcon icon={FiIcons.FiShoppingBag} className="w-5 h-5" />
                <AnimatePresence>
                  {cartCount > 0 && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-stone-900 text-white text-[8px] flex items-center justify-center rounded-full font-bold"
                    >
                      {cartCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '-100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '-100%' }}
            className="fixed inset-0 z-[120] bg-stone-50 p-8 flex flex-col"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-serif text-2xl">Menu</span>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2" aria-label="Close menu">
                <SafeIcon icon={FiIcons.FiX} className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-10">
              {navLinks.map((link) => (
                <a 
                  key={link.name} 
                  href={link.href} 
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-serif text-5xl text-stone-900 tracking-tight"
                >
                  {link.name}
                </a>
              ))}
              <div className="mt-12 pt-12 border-t border-stone-200 space-y-6">
                <a href="#" className="block text-xs uppercase tracking-[0.3em] font-bold text-stone-500">My Account</a>
                <a href="#" className="block text-xs uppercase tracking-[0.3em] font-bold text-stone-500">Customer Care</a>
                <a href="#" className="block text-xs uppercase tracking-[0.3em] font-bold text-stone-500">Journal</a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;